import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError, formatDate } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { GazetteIssue, GazetteIssueStatus, GazetteSubscriptionCreated, Page, SubscriptionChannel } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

const CHANNELS: SubscriptionChannel[] = ['EMAIL', 'SMS', 'WHATSAPP'];

function todayISODate(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * GET /api/v1/m38/gazette-issues — public sees PUBLISHED-only; a signed-in
 * GAZETTE_EDITOR passing ?status= also sees COMPILING drafts (see
 * m38-attorney-general/src/routes/gazette.ts's optionalAuth). The
 * subscribe/unsubscribe panel is genuinely public (no login) too.
 */
export function Gazette() {
  const { persona } = useAuth();
  const isEditor = persona?.roles.includes('GAZETTE_EDITOR') ?? false;
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<GazetteIssueStatus | ''>('');
  const [showCreate, setShowCreate] = useState(false);
  const [showSubscribe, setShowSubscribe] = useState(false);

  const params = new URLSearchParams({ limit: '50' });
  if (q.trim()) params.set('q', q.trim());
  if (isEditor && status) params.set('status', status);
  const listQ = useApiQuery<Page<GazetteIssue>>(`/api/v1/m38/gazette-issues?${params.toString()}`);

  return (
    <div>
      <PageHeader
        title="Government Gazette"
        subtitle="Published issues are public record; a Gazette Editor may also compile drafts here."
        actions={
          <>
            <button className="btn-secondary" onClick={() => setShowSubscribe(true)}>
              Subscribe to alerts
            </button>
            {isEditor ? (
              <button className="btn-primary" onClick={() => setShowCreate(true)}>
                + New draft issue
              </button>
            ) : null}
          </>
        }
      />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label="Issue number contains" className="min-w-[220px] flex-1">
          <input className="field-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. 34" />
        </Field>
        {isEditor ? (
          <Field label="Status (editor only)" className="!mb-0">
            <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value as GazetteIssueStatus | '')}>
              <option value="">Published only</option>
              <option value="COMPILING">Compiling (drafts)</option>
              <option value="PUBLISHED">Published</option>
            </select>
          </Field>
        ) : null}
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No gazette issues found" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Issue</th>
                <th className="table-head">Date</th>
                <th className="table-head">Status</th>
                <th className="table-head">Permanent URL</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((g) => (
                <tr key={g.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/ag/gazette/${g.id}`)}>
                  <td className="table-cell font-medium">
                    <Link to={`/ag/gazette/${g.id}`} className="text-brand-700 hover:underline" onClick={(e) => e.stopPropagation()}>
                      No. {g.issueNumber} of {g.issueYear}
                    </Link>
                  </td>
                  <td className="table-cell text-xs">{formatDate(g.issueDate)}</td>
                  <td className="table-cell">
                    <StatusBadge status={g.status} />
                  </td>
                  <td className="table-cell text-xs text-slate-500">{g.permanentUrl ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate ? (
        <CreateIssueModal
          onClose={() => setShowCreate(false)}
          onDone={(id) => {
            setShowCreate(false);
            navigate(`/ag/gazette/${id}`);
          }}
        />
      ) : null}
      {showSubscribe ? <SubscribeModal onClose={() => setShowSubscribe(false)} /> : null}
    </div>
  );
}

function CreateIssueModal({ onClose, onDone }: { onClose: () => void; onDone: (id: string) => void }) {
  const [issueNumber, setIssueNumber] = useState('');
  const [issueDate, setIssueDate] = useState(todayISODate());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await api.post<GazetteIssue>('/api/v1/m38/gazette-issues', {
        issueNumber,
        issueDate: new Date(`${issueDate}T00:00:00.000Z`).toISOString(),
      });
      onDone(created.id);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to open issue');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Open a new draft gazette issue" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Issue number">
          <input className="field-input" value={issueNumber} onChange={(e) => setIssueNumber(e.target.value)} placeholder="e.g. 34" />
        </Field>
        <Field label="Issue date">
          <input type="date" className="field-input" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !issueNumber.trim()}>
            {busy ? 'Opening…' : 'Open issue'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function SubscribeModal({ onClose }: { onClose: () => void }) {
  const [mode, setMode] = useState<'subscribe' | 'unsubscribe'>('subscribe');
  const [channel, setChannel] = useState<SubscriptionChannel>('EMAIL');
  const [contact, setContact] = useState('');
  const [topics, setTopics] = useState('');
  const [created, setCreated] = useState<GazetteSubscriptionCreated | null>(null);
  const [unsubId, setUnsubId] = useState('');
  const [unsubToken, setUnsubToken] = useState('');
  const [unsubDone, setUnsubDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const subscribe = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<GazetteSubscriptionCreated>('/api/v1/m38/gazette/subscriptions', {
        channel,
        contact,
        topics: topics
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      });
      setCreated(res);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Subscription failed');
    } finally {
      setBusy(false);
    }
  };

  const unsubscribe = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m38/gazette/subscriptions/${unsubId.trim()}/unsubscribe`, { unsubscribeToken: unsubToken.trim() });
      setUnsubDone(true);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Unsubscribe failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Gazette alert subscription" onClose={onClose}>
      <div className="space-y-3">
        <div className="flex gap-1 rounded-lg bg-slate-200/60 p-1 text-sm">
          <button
            className={`flex-1 rounded-md px-3 py-1.5 font-semibold transition ${mode === 'subscribe' ? 'bg-white shadow-sm' : 'text-slate-500'}`}
            onClick={() => setMode('subscribe')}
          >
            Subscribe
          </button>
          <button
            className={`flex-1 rounded-md px-3 py-1.5 font-semibold transition ${mode === 'unsubscribe' ? 'bg-white shadow-sm' : 'text-slate-500'}`}
            onClick={() => setMode('unsubscribe')}
          >
            Unsubscribe
          </button>
        </div>
        {error ? <ErrorNote message={error} /> : null}

        {mode === 'subscribe' ? (
          created ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3.5 text-sm text-emerald-800">
              <p className="font-semibold">Subscribed.</p>
              <p className="mt-1 text-xs">
                Save these to unsubscribe later — subscription id <code>{created.id}</code>, token{' '}
                <code className="break-all">{created.unsubscribeToken}</code>.
              </p>
            </div>
          ) : (
            <>
              <p className="text-xs text-slate-500">No sign-in required — public SeyNotify subscription.</p>
              <Field label="Channel">
                <select className="field-input" value={channel} onChange={(e) => setChannel(e.target.value as SubscriptionChannel)}>
                  {CHANNELS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Contact (email or E.164 phone)">
                <input className="field-input" value={contact} onChange={(e) => setContact(e.target.value)} placeholder="e.g. you@example.sc" />
              </Field>
              <Field label="Topics (comma separated notice categories, optional — blank = all)">
                <input className="field-input" value={topics} onChange={(e) => setTopics(e.target.value)} placeholder="e.g. ACT_COMMENCEMENT, LEGAL_NOTICE" />
              </Field>
              <div className="flex justify-end gap-2 pt-2">
                <button className="btn-secondary" onClick={onClose}>
                  Close
                </button>
                <button className="btn-primary" onClick={subscribe} disabled={busy || contact.trim().length < 3}>
                  {busy ? 'Subscribing…' : 'Subscribe'}
                </button>
              </div>
            </>
          )
        ) : unsubDone ? (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3.5 text-sm text-emerald-800">You are unsubscribed.</div>
        ) : (
          <>
            <Field label="Subscription id">
              <input className="field-input" value={unsubId} onChange={(e) => setUnsubId(e.target.value)} />
            </Field>
            <Field label="Unsubscribe token">
              <input className="field-input" value={unsubToken} onChange={(e) => setUnsubToken(e.target.value)} />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <button className="btn-secondary" onClick={onClose}>
                Close
              </button>
              <button className="btn-danger" onClick={unsubscribe} disabled={busy || !unsubId.trim() || !unsubToken.trim()}>
                {busy ? 'Unsubscribing…' : 'Unsubscribe'}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
